/**
 * Modal — a focus-trapped dialog over a scrim, with no provider required.
 *
 * ── Why a second modal ─────────────────────────────────────────────────────
 * `panels/ModalPanel` already exists, and this does NOT replace it. But it has
 * 3 importers while 152 files hand-roll `fixed inset-0 bg-black`, and the cause
 * is structural: ModalPanel needs `PanelProvider` + `usePanels`, because it is
 * part of a managed system where panels can be minimized, stacked and restored.
 * A dialog you cannot render without wiring a provider is a dialog nobody uses,
 * so people wrote their own — 152 times, each with its own scrim opacity, its
 * own escape handling (usually none), and no focus management at all.
 *
 * So: ModalPanel keeps the managed/minimizable system. This is the ordinary
 * dialog — mount it, pass `open`, done.
 *
 * Focus is trapped without a dependency: focus moves in on open, Tab cycles
 * within, Escape closes, and the previously focused element is restored on
 * close. That last part is the one hand-rolled modals never do, and it is what
 * strands keyboard users at the top of the page after closing a dialog.
 */
import React, { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { FOCUS_RING } from '../mono/primitives';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Mono eyebrow above the title. */
  label?: React.ReactNode;
  /** Sentence under the title. Wired as the dialog's accessible description. */
  description?: React.ReactNode;
  children?: React.ReactNode;
  /** Action cluster along the bottom edge, right-aligned. */
  footer?: React.ReactNode;
  size?: ModalSize;
  /** Set false for a dialog the user must resolve — a destructive confirm. */
  dismissible?: boolean;
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  label,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className = '',
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();

  const close = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  // Remember what had focus, move focus into the dialog, restore on close.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const first = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? dialogRef.current)?.focus();
    return () => restoreRef.current?.focus?.();
  }, [open]);

  // Escape closes; Tab cycles inside the dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, close]);

  // Stop the page behind from scrolling while the dialog is up.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60"
        onClick={close}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`relative flex max-h-[90vh] w-full flex-col border bg-[var(--bg-raised)] ${SIZE[size]} ${className}`}
        style={{ borderColor: 'var(--rule-bright)' }}
      >
        <header
          className="flex items-start justify-between gap-4 border-b px-5 py-4"
          style={{ borderColor: 'var(--rule)' }}
        >
          <div className="min-w-0">
            {label && (
              <p className="font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
                {label}
              </p>
            )}
            <h2
              id={titleId}
              className={`font-serif text-lg leading-tight ${label ? 'mt-0.5' : ''}`}
              /* Inline: `.dark h1…h6` outweighs a plain colour class. */
              style={{ color: 'var(--text-pri)' }}
            >
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-sm leading-relaxed text-[var(--text-ter)]">
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`-mr-1 -mt-1 shrink-0 p-1.5 text-[var(--text-dim)] transition-colors hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] ${FOCUS_RING}`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </header>

        {children && <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>}

        {footer && (
          <footer
            className="flex items-center justify-end gap-2 border-t px-5 py-4"
            style={{ borderColor: 'var(--rule)' }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}
